/**
 * Chart of Accounts mapping use cases.
 * Shared reads for the Business Partner screens (service providers and
 * portfolio companies alike).
 */

import { fetchAllPaginated } from "@/utils/fetchAllPaginated";

/**
 * The slice of the API client this use case needs, described structurally so
 * callers can pass `api.serviceProviders` or `api.portfolioCompanies` without
 * dragging in the whole client type. Generic in the mapping row: each caller
 * maps the raw rows into its own shape.
 */
export type CoaMappingListApi<TMapping> = {
    listCoAMappings: (
        ownerId: string,
        params?: { page?: number; page_size?: number },
    ) => Promise<{ items: TMapping[]; total: number }>;
};

/**
 * Rows per request. The `/coa` endpoints accept up to 10000, so this is the
 * fewest round-trips they allow — it is not a ceiling on the result, which is
 * bounded only by how many mappings the business partner has.
 */
export const COA_MAPPING_PAGE_SIZE = 10000;

/**
 * Every CoA mapping for a business partner, paging until the set is complete.
 *
 * Deliberately un-capped. The Business Partner screen renders this as the whole
 * mapping set and its Download button serialises exactly what was loaded, while
 * a CoA upload *replaces* the whole set rather than merging into it — so a read
 * that stopped at one page would produce a download that silently deletes every
 * mapping past the cut-off when it is edited and re-uploaded. Raising the page
 * size only moves that cliff; paging removes it.
 */
export async function fetchAllCoaMappings<TMapping>(
    api: CoaMappingListApi<TMapping>,
    ownerId: string,
): Promise<TMapping[]> {
    return fetchAllPaginated(
        (page, page_size) => api.listCoAMappings(ownerId, { page, page_size }),
        COA_MAPPING_PAGE_SIZE,
    );
}
